import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import Button from '@/shared/components/Button';
import Toggle from 'react-toggle';
import api from '@/shared/api';
import config from '@/config';
import intlShape from '@/shared/utils/intlShape';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from './assignmentTemplateMessages';
import TranslateListItem from '../../../shared/components/translations/TranslateListItem';
import translationHelper from '../../../shared/components/translations/TranslationHelper';

class AddAssignmentTemplateModal extends Component {
  state = {
    template: {
      name: '',
      appliesToPerson: true,
      appliesToSoleProprietor: true,
      appliesToCompany: true,
      allowMultiple: false,
    },
    translations: null,
    showValidation: false,
    saving: false,
  };

  componentDidMount() {
    translationHelper.create('assignmenttemplate').then(translations => {
      this.setState({ translations });
    });
  }

  save = () => {
    const { saving, template, translations } = this.state;
    const { onClose } = this.props;
    if (!this.isValid() || saving) return '';
    this.setState({ saving: true });

    return api.base
      .post(`${config.appUrls.api}/api/AssignmentTemplate`, template)
      .then(({ data }) => {
        if (!data) return data;
        if (!template.id && data.id) {
          // Create request is finished, save translations before handling the result
          return translationHelper
            .save(translationHelper.setKey(translations, 'assignmenttemplate', data.id))
            .then(() => data); // Return the original result (not the translation result)
        }
        return data;
      })
      .then(
        data => {
          if (!data) return '';
          onClose(data);
          return data;
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
          return error;
        },
      )
      .finally(() => {
        if (typeof this !== 'undefined') this.setState({ saving: false });
      });
  };

  updateTemplate = changes => {
    this.setState(({ template }) => ({ template: { ...template, ...changes } }));
  };
  changeName = name => this.updateTemplate({ name });
  updateTranslations = translations => this.setState({ translations });

  showValidation = () => {
    const { showValidation } = this.state;
    if (!showValidation) this.setState({ showValidation: true });
  };

  isValid = () => {
    const { template, translations } = this.state;

    if (translations && !translationHelper.validate(translations)) {
      return false;
    }
    return (
      !!template &&
      !!template.name &&
      (template.appliesToPerson || template.appliesToSoleProprietor || template.appliesToCompany) // At least 1 checked
    );
  };

  render() {
    const { onClose, intl } = this.props;
    const { template, showValidation, saving, translations } = this.state;

    const isValid = this.isValid();
    const appliesToInvalid =
      !template.appliesToPerson && !template.appliesToSoleProprietor && !template.appliesToCompany;
    return (
      <Modal backdrop="static" show onHide={() => onClose(null)} keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.AddAssignmentTemplate} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-horizontal">
            <div className={classnames('form-group')}>
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.Code} />
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  value={template.code}
                  onChange={e => this.updateTemplate({ code: e.target.value })}
                  className={classnames('form-control')}
                  placeholder={intl.formatMessage(messages.Code)}
                  autoComplete="off"
                  disabled={saving}
                />
              </div>
            </div>
            {translations && (
              <TranslateListItem
                translations={translations}
                label={intl.formatMessage(messages.Name)}
                labelClass="col-sm-4"
                inputClass="col-sm-8"
                onChange={this.updateTranslations}
                onDefaultChanged={this.changeName}
                showValidation={showValidation}
                disabled={saving}
                maxLength={100}
              />
            )}
            <div
              className={classnames('form-group', {
                'has-error': showValidation && appliesToInvalid,
              })}
            >
              <div className="col-sm-8 col-sm-offset-4">
                <label className="react-toggle-wrapper">
                  <Toggle
                    checked={template.appliesToPerson}
                    onChange={e => this.updateTemplate({ appliesToPerson: e.target.checked })}
                  />
                  <FormattedMessage {...messages.AppliesToPerson} />
                </label>
              </div>
            </div>
            <div
              className={classnames('form-group', {
                'has-error': showValidation && appliesToInvalid,
              })}
            >
              <div className="col-sm-8 col-sm-offset-4">
                <label className="react-toggle-wrapper">
                  <Toggle
                    checked={template.appliesToSoleProprietor}
                    onChange={e =>
                      this.updateTemplate({ appliesToSoleProprietor: e.target.checked })
                    }
                  />
                  <FormattedMessage {...messages.AppliesToSoleProprietor} />
                </label>
              </div>
            </div>
            <div
              className={classnames('form-group', {
                'has-error': showValidation && appliesToInvalid,
              })}
            >
              <div className="col-sm-8 col-sm-offset-4">
                <label className="react-toggle-wrapper">
                  <Toggle
                    checked={template.appliesToCompany}
                    onChange={e => this.updateTemplate({ appliesToCompany: e.target.checked })}
                  />
                  <FormattedMessage {...messages.AppliesToCompany} />
                </label>
              </div>
            </div>
            <div className={classnames('form-group')}>
              <div className="col-sm-8 col-sm-offset-4">
                <label className="react-toggle-wrapper">
                  <Toggle
                    checked={template.allowMultiple}
                    onChange={e => this.updateTemplate({ allowMultiple: e.target.checked })}
                  />
                  <FormattedMessage {...messages.AllowMultiplePerRelation} />
                </label>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving} onClick={() => onClose(null)}>
            <FormattedMessage {...messages.Cancel} />
          </Button>
          <Button
            onClick={() => this.save()}
            disabled={showValidation && !isValid}
            onFocus={this.showValidation}
            onMouseOver={this.showValidation}
            btnClassName="btn-inverse"
            showProgress
          >
            <FormattedMessage {...messages.Add} />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

AddAssignmentTemplateModal.propTypes = {
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default injectIntl(AddAssignmentTemplateModal);
